import {
  motion,
  useReducedMotion,
  type Variants,
  type HTMLMotionProps,
} from 'framer-motion';

/**
 * Shared scroll-reveal + stagger primitives for the marketing site.
 *
 * Every effect is gated by `useReducedMotion()` so that visitors who ask their
 * OS to reduce motion get an instant, static render (matching the CSS
 * `prefers-reduced-motion` guard in `base.css`). Animations only touch
 * transform/opacity — never layout — so they never shift content.
 */

const EASE = [0.22, 1, 0.36, 1] as const;

const revealVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const staggerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
};

const staggerItemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

const VIEWPORT = { once: true, margin: '-10% 0px' } as const;

interface RevealProps extends HTMLMotionProps<'div'> {
  /** Seconds to delay the reveal after it enters the viewport. */
  delay?: number;
}

/** Fade + rise into view once, when scrolled ~10% into the viewport. */
export function Reveal({ delay = 0, children, ...props }: RevealProps) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : 'hidden'}
      whileInView={reduced ? undefined : 'visible'}
      viewport={VIEWPORT}
      variants={reduced ? undefined : revealVariants}
      transition={!reduced && delay ? { delay } : undefined}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/** Wraps a group of `StaggerItem`s so children reveal in sequence. */
export function Stagger({ children, ...props }: HTMLMotionProps<'div'>) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : 'hidden'}
      whileInView={reduced ? undefined : 'visible'}
      viewport={VIEWPORT}
      variants={reduced ? undefined : staggerVariants}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/** A single item inside a `Stagger`. */
export function StaggerItem({ children, ...props }: HTMLMotionProps<'div'>) {
  const reduced = useReducedMotion();
  return (
    <motion.div variants={reduced ? undefined : staggerItemVariants} {...props}>
      {children}
    </motion.div>
  );
}

/** Re-export so sections can compose bespoke motion without a second import. */
export { motion, useReducedMotion };
